// @ts-nocheck
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Network } from 'lucide-react';
import { isIndividualSport } from '@staty/shared/utils/sportCategory';
import SectionCard from './SectionCard';

// Numeric text input per repo rule (never type="number" — scroll wheel corrupts values).
function CountInput({ id, label, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        className="w-28"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      />
    </div>
  );
}

export default function RosterRequirementsSection({ d, sport }) {
  const { draft, setField, dirtyBySection, savingSection, saveSection, contestType, parentSynced } = d;
  const parentLabel = contestType === 'tournament' ? 'tournament' : 'league';

  return (
    <SectionCard
      id="roster"
      title="Team & Roster Requirements"
      description="Who a team must include to register."
      dirty={dirtyBySection.roster}
      saving={savingSection === 'roster'}
      onSave={() => saveSection('roster')}
    >
      <Alert>
        <Network className="h-4 w-4" />
        <AlertTitle>Applies to the whole {parentLabel}</AlertTitle>
        <AlertDescription>
          These requirements are saved to the {parentLabel} itself and inherited by every division in
          it (a division&apos;s own settings override them). They are not just registration settings.
          {!parentSynced && (
            <span className="mt-1 block font-medium text-warning">
              The linked {parentLabel} hasn&apos;t loaded yet. Saving now updates only the registration copy.
            </span>
          )}
        </AlertDescription>
      </Alert>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Switch id="req-players" checked={!!draft.requirePlayers} onCheckedChange={(v) => setField('requirePlayers', v)} />
          <Label htmlFor="req-players" className="text-base font-medium">Collect players information</Label>
        </div>
        {draft.requirePlayers && (
          <div className="space-y-4 pl-10">
            <div className="flex gap-4">
              <CountInput id="min-players" label="Min players" value={draft.minPlayers} onChange={(v) => setField('minPlayers', v)} />
              <CountInput id="max-players" label="Max players" value={draft.maxPlayers} onChange={(v) => setField('maxPlayers', v)} />
            </div>
            {!isIndividualSport(sport) && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Switch
                    id="player-autocomplete"
                    checked={!!draft.playerAutoComplete}
                    onCheckedChange={(v) => setField('playerAutoComplete', v)}
                  />
                  <Label htmlFor="player-autocomplete">Enable player auto-complete</Label>
                </div>
                <p className="pl-10 text-xs text-muted-foreground">
                  While typing a player&apos;s name during registration, team reps see suggestions from
                  players registered to that team in past seasons; selecting one auto-fills the rest of
                  the player&apos;s info.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Switch id="req-coaches" checked={!!draft.allowCoaches} onCheckedChange={(v) => setField('allowCoaches', v)} />
          <Label htmlFor="req-coaches" className="text-base font-medium">Collect coaches information</Label>
        </div>
        {draft.allowCoaches && (
          <div className="flex gap-4 pl-10">
            <CountInput id="min-coaches" label="Min coaches" value={draft.minCoaches} onChange={(v) => setField('minCoaches', v)} />
            <CountInput id="max-coaches" label="Max coaches" value={draft.maxCoaches} onChange={(v) => setField('maxCoaches', v)} />
          </div>
        )}
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Switch
            id="req-reps"
            checked={!!draft.requireTeamRepresentatives}
            onCheckedChange={(v) => setField('requireTeamRepresentatives', v)}
          />
          <Label htmlFor="req-reps" className="text-base font-medium">Collect team representatives information</Label>
        </div>
        {draft.requireTeamRepresentatives && (
          <div className="flex gap-4 pl-10">
            <CountInput
              id="min-reps"
              label="Min representatives"
              value={draft.minTeamRepresentatives}
              onChange={(v) => setField('minTeamRepresentatives', v)}
            />
            <CountInput
              id="max-reps"
              label="Max representatives"
              value={draft.maxTeamRepresentatives}
              onChange={(v) => setField('maxTeamRepresentatives', v)}
            />
          </div>
        )}
      </div>

      {/* The "Exclude team name" switch moved to Form Fields → Team form as
          "Require team name" (owner 2026-09-29) — one home, next to its live
          preview. Drop this pointer after a release cycle. */}
      <p className="text-xs text-muted-foreground">
        Looking for the team-name setting? It moved to Form Fields → Team form.
      </p>

      {/* Registration-OWN setting (ADR 0054): saved to registration.config only,
          never mirrored to the parent tournament/league config. */}
      <div className="space-y-2 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <Switch
            id="allow-free-agents"
            checked={!!draft.allowFreeAgents}
            onCheckedChange={(v) => setField('allowFreeAgents', v)}
          />
          <Label htmlFor="allow-free-agents" className="text-base font-medium">
            Allow individual (free agent) signups
          </Label>
        </div>
        <p className="pl-10 text-sm text-muted-foreground">
          People without a team can sign up and you assign them to teams.
        </p>
      </div>
    </SectionCard>
  );
}
